import React, {useRef, useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import useAutoSave from 'components/customer/RoomPlanner/lib/useAutoSave';
import * as THREE from 'three';
import DistanceEditor, {MobileControls} from './DistanceEditor';
import DistanceBubbleEditor, {DistanceBubbleEdit} from './DistanceBubbleEditor';
import type {DistanceBubbleRect} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {
    Direction,
    useRoomPlannerContext,
} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {applyVerticalClamping} from 'components/customer/RoomPlanner/helpers/keyboardControls';
import {
    calculateMovementVector,
    RepositionDirection,
} from 'components/customer/RoomPlanner/helpers/positionEditorMovement';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {useAppSelector} from 'store/customer';
import {getRoomType} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {handleFillerSwitching} from 'components/customer/RoomPlanner/helpers/fillerSwitching';
import {
    isScene,
    syncOverlaysOnCabinetMove,
} from 'components/customer/RoomPlanner/helpers/syncOverlayOnCabinetMove';
import {
    mobileLandscape,
    mobileLandscapeAndPortraitDevices,
} from 'shared/Breakpoints';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import {Col} from 'react-bootstrap';
import {CancelButton} from 'shared/components/CancelButton';
import {CBCButton} from 'shared/helpers';

interface CustomUserData {
    move?: (position: THREE.Vector3) => void;
    resetArrowVisibility?: () => void;
    onArrowClick?: (params: {
        direction: RepositionDirection;
        screenX: number;
        screenY: number;
        arrowMesh: THREE.Mesh;
    }) => void;
    currentDirection?: string;
    movementArrows?: THREE.Mesh[];
    onDistanceClick?: (params: {
        direction: string;
        screenX: number;
        screenY: number;
        measurement: string;
        measurementObject: THREE.Object3D;
        bubble?: DistanceBubbleRect;
    }) => void;
    [key: string]: unknown;
}

const isHorizontalWall = (
    direction: string
): direction is DistanceBubbleEdit['wall'] =>
    direction === 'left' ||
    direction === 'right' ||
    direction === 'front' ||
    direction === 'back';

const mapDirectionByOrientation = (
    direction: 'right' | 'left' | 'back' | 'front' | 'up' | 'down',
    currentDirection?: string,
    isAppliedPanel?: boolean,
    isUpperProduct?: boolean
): 'right' | 'left' | 'back' | 'front' | 'up' | 'down' => {
    if (
        isAppliedPanel &&
        isUpperProduct &&
        (direction === 'up' || direction === 'down')
    ) {
        return direction;
    }
    if (currentDirection === 'left') {
        if (direction === 'front') return 'right';
        if (direction === 'right') return 'back';
        if (direction === 'back') return 'left';
        if (direction === 'left') return 'front';
    } else if (currentDirection === 'right') {
        if (direction === 'front') return 'left';
        if (direction === 'left') return 'back';
        if (direction === 'back') return 'right';
        if (direction === 'right') return 'front';
    } else if (currentDirection === 'back') {
        if (direction === 'front') return 'back';
        if (direction === 'back') return 'front';
        if (direction === 'left') return 'right';
        if (direction === 'right') return 'left';
    }
    return direction;
};

// Helper function to adjust editor position based on arrow direction
const adjustEditorPosition = (
    screenX: number,
    screenY: number,
    direction: 'right' | 'left' | 'back' | 'front' | 'up' | 'down'
): {x: number; y: number} => {
    let adjustedX = screenX;
    let adjustedY = screenY;

    switch (direction) {
        case 'right':
            adjustedX += 50;
            break;
        case 'left':
            adjustedX -= 150;
            break;
        case 'front':
            adjustedY += 30;
            adjustedX -= 75;
            break;
        case 'back':
            adjustedY -= 50;
            adjustedX -= 75;
            break;
        case 'up':
        case 'down':
            adjustedX += 50;
            adjustedY -= 10;
            break;
    }

    return {x: adjustedX, y: adjustedY};
};

const getAxisValue = (vector: THREE.Vector3, axis: 'x' | 'y' | 'z'): number => {
    switch (axis) {
        case 'x':
            return vector.x;
        case 'y':
            return vector.y;
        case 'z':
            return vector.z;
    }
};

const setAxisValue = (
    vector: THREE.Vector3,
    axis: 'x' | 'y' | 'z',
    value: number
) => {
    switch (axis) {
        case 'x':
            vector.x = value;
            break;
        case 'y':
            vector.y = value;
            break;
        case 'z':
            vector.z = value;
            break;
    }
};

const addAxisValue = (
    vector: THREE.Vector3,
    axis: 'x' | 'y' | 'z',
    delta: number
) => {
    setAxisValue(vector, axis, getAxisValue(vector, axis) + delta);
};

const movePreviewPanel = (
    ghostCabinet: CabinetMesh,
    axis: 'x' | 'y' | 'z',
    offset: number
) => {
    if (!ghostCabinet) return;

    ghostCabinet.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            const name = child.name?.toLowerCase() || '';
            const isPanel =
                name.includes('panel') ||
                name.includes('face') ||
                name.includes('front') ||
                child.userData?.isPanelProduct === true ||
                child.userData?.isManualPanel === true;

            if (isPanel) {
                addAxisValue(child.position, axis, offset);
                child.updateMatrixWorld();
            }
        }
    });
};

const PositionEditor = () => {
    const {
        selectedCabinetGroup,
        cabinetGroupListRef,
        activeDirection,
        setActiveDirection,
        editorMode,
        setEditorMode,
    } = useRoomPlannerContext();
    const {debouncedSave} = useAutoSave();
    const roomType = useAppSelector(getRoomType);

    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileDevice = isMobileLandscape || isPortraitDevices;

    const editorRef = useRef<HTMLDivElement>(null);
    const ghostCabinetRef = useRef<CabinetMesh | null>(null);
    // clone() shares original materials; we replace some with clones and must
    // dispose only those owned copies when the ghost is cleared.
    const ghostMaterialsRef = useRef<THREE.Material[]>([]);
    const prevActiveDirectionRef = useRef<Direction | null>(null);

    const [editorPosition, setEditorPosition] = useState({x: 0, y: 0});

    // Wall-distance pill being edited in place, if any.
    const [bubbleEdit, setBubbleEdit] = useState<DistanceBubbleEdit | null>(
        null
    );
    const closeBubbleEdit = useCallback(() => setBubbleEdit(null), []);

    // The edit belongs to the cabinet it was opened on.
    useEffect(() => {
        setBubbleEdit(null);
    }, [selectedCabinetGroup]);

    // Drag state
    const isDraggingRef = useRef(false);
    const dragStartPosRef = useRef({x: 0, y: 0});
    const dragOffsetRef = useRef({x: 0, y: 0});

    // Add refs for input fields
    const inputRefs: Record<Direction, React.RefObject<HTMLInputElement>> = {
        right: useRef<HTMLInputElement>(null),
        left: useRef<HTMLInputElement>(null),
        back: useRef<HTMLInputElement>(null),
        front: useRef<HTMLInputElement>(null),
        up: useRef<HTMLInputElement>(null),
        down: useRef<HTMLInputElement>(null),
    };

    // New state for movement amount
    const [movementAmount, setMovementAmount] = useState('100');

    // Update overlay pose from the live mesh (same path as keyboard / layout).
    // Do not copy group.position — placeholders use a cabinet-like origin, not the GLB pivot.
    const updateArrowPositions = useCallback(() => {
        if (!selectedCabinetGroup) return;

        const scene = selectedCabinetGroup.parent;
        if (!isScene(scene)) return;

        syncOverlaysOnCabinetMove(selectedCabinetGroup, scene);

        const selectedId = selectedCabinetGroup.userData.id as string;
        scene.children.forEach((child: THREE.Object3D) => {
            if (
                child.name !== 'cabinetMovementArrows' ||
                child.userData?.id !== selectedId
            ) {
                return;
            }

            child.children.forEach((arrow) => {
                if (arrow.userData?.isArrow && arrow.userData.isHovered) {
                    if (typeof arrow.userData.onHoverEnd === 'function') {
                        (arrow.userData.onHoverEnd as () => void)();
                    }
                    arrow.userData.isHovered = false;
                }
            });
        });
    }, [selectedCabinetGroup]);

    const removeGhostCabinet = useCallback(() => {
        const ghost = ghostCabinetRef.current;
        if (!ghost) return;

        ghost.parent?.remove(ghost);
        ghostMaterialsRef.current.forEach((material) => material.dispose());
        ghostMaterialsRef.current = [];
        ghostCabinetRef.current = null;
    }, []);

    // Function to render/update ghost cabinet preview
    // Ghost is semi-transparent to indicate preview position, original stays solid
    const renderGhostCabinet = useCallback(
        (
            isValid: boolean,
            targetPos: THREE.Vector3,
            scene: THREE.Scene | null,
            shouldShow = true
        ) => {
            if (!selectedCabinetGroup || !scene || !shouldShow) {
                removeGhostCabinet();
                return;
            }

            // Create ghost cabinet only once, or recreate if needed.
            // Cabinets draw a wireframe (edge lines). Appliance GLBs have no
            // edges, so a later pass that hides every mesh leaves the preview
            // invisible or, if a stray mesh survives, sitting on the pivot.
            if (!ghostCabinetRef.current) {
                const ghostCabinet = selectedCabinetGroup.clone();
                ghostCabinetRef.current = ghostCabinet;

                const edges: THREE.LineSegments[] = [];
                const meshes: THREE.Mesh[] = [];
                ghostCabinet.traverse((child) => {
                    if (child instanceof THREE.LineSegments) {
                        edges.push(child as THREE.LineSegments);
                    } else if (child instanceof THREE.Mesh) {
                        meshes.push(child as THREE.Mesh);
                    }
                });

                edges.forEach((edge) => {
                    edge.visible = true;
                    if (edge.material instanceof THREE.LineBasicMaterial) {
                        const clonedMat = edge.material.clone();
                        clonedMat.color = new THREE.Color('#000');
                        edge.material = clonedMat;
                        ghostMaterialsRef.current.push(clonedMat);
                    }
                });

                meshes.forEach((mesh) => {
                    if (edges.length > 0) {
                        mesh.visible = false;
                        return;
                    }
                    const source = mesh.material;
                    const cloned = (
                        Array.isArray(source) ? source : [source]
                    ).map((material) => {
                        const next = material.clone();
                        next.transparent = true;
                        next.opacity = Math.min(next.opacity, 0.45);
                        next.depthWrite = false;
                        return next;
                    });
                    mesh.material = Array.isArray(source) ? cloned : cloned[0];
                    ghostMaterialsRef.current.push(...cloned);
                    mesh.visible = true;
                });

                scene.add(ghostCabinetRef.current);
            }

            ghostCabinetRef.current.position.copy(targetPos);
        },
        [selectedCabinetGroup, removeGhostCabinet]
    );

    // Get cabinet dimensions
    // Updated Move functions to use the movement amount

    const handleMoveWithAmount = useCallback(
        (direction: string) => {
            if (!selectedCabinetGroup) return;

            const amount =
                movementAmount === '' ? 0 : parseFloat(movementAmount);
            if (isNaN(amount)) return;

            const isAppliedPanel =
                selectedCabinetGroup.userData.isPanelProduct ||
                selectedCabinetGroup.userData.isManualPanel ||
                selectedCabinetGroup.userData.isPanelProductWithWidth ||
                selectedCabinetGroup.userData.isPanelProductWithLength;
            const isUpperProduct = selectedCabinetGroup.userData.isUpperProduct;

            const movementVector = calculateMovementVector(
                direction as Direction,
                amount,
                isAppliedPanel,
                isUpperProduct
            );

            if (
                isAppliedPanel &&
                isUpperProduct &&
                (direction === 'up' || direction === 'down')
            ) {
                let panelMesh: THREE.Mesh | null = null;

                selectedCabinetGroup.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        const name = child.name?.toLowerCase() || '';
                        const isPanelMesh =
                            name.includes('panel') ||
                            name.includes('face') ||
                            name.includes('front') ||
                            child.userData?.isPanelProduct === true ||
                            child.userData?.isManualPanel === true ||
                            child.userData?.type === 'panel';

                        if (isPanelMesh) {
                            panelMesh = child as THREE.Mesh<
                                THREE.BufferGeometry,
                                THREE.Material | THREE.Material[]
                            >;
                        }
                    }
                });

                if (panelMesh) {
                    const mesh: THREE.Mesh = panelMesh;
                    const axis: 'x' | 'y' | 'z' =
                        direction === 'up' || direction === 'down'
                            ? 'y'
                            : direction === 'left' || direction === 'right'
                              ? 'x'
                              : 'z';

                    const worldToLocal = selectedCabinetGroup.matrixWorld
                        .clone()
                        .invert();

                    const cabinetBox = new THREE.Box3()
                        .setFromObject(selectedCabinetGroup)
                        .applyMatrix4(worldToLocal);

                    const cabinetMin = getAxisValue(cabinetBox.min, axis);
                    const cabinetMax = getAxisValue(cabinetBox.max, axis);

                    const panelBox = new THREE.Box3()
                        .setFromObject(mesh)
                        .applyMatrix4(worldToLocal);

                    const panelSize =
                        getAxisValue(panelBox.max, axis) -
                        getAxisValue(panelBox.min, axis);

                    let newPanelPos =
                        getAxisValue(mesh.position, axis) +
                        getAxisValue(movementVector, axis);

                    const relativeMin = cabinetMin + panelSize / 2;
                    const relativeMax = cabinetMax - panelSize / 2;

                    newPanelPos = Math.max(
                        relativeMin,
                        Math.min(newPanelPos, relativeMax)
                    );

                    const actualMovement =
                        newPanelPos - getAxisValue(mesh.position, axis);

                    if (actualMovement !== 0) {
                        setAxisValue(mesh.position, axis, newPanelPos);
                        mesh.updateMatrixWorld();

                        mesh.children.forEach((child) => {
                            addAxisValue(child.position, axis, actualMovement);
                            child.updateMatrixWorld();
                        });

                        selectedCabinetGroup.traverse((child) => {
                            if (child !== mesh && child instanceof THREE.Mesh) {
                                const childName =
                                    child.name?.toLowerCase() || '';
                                if (
                                    childName.includes('handle') ||
                                    childName.includes('deco') ||
                                    childName.includes('trim')
                                ) {
                                    addAxisValue(
                                        child.position,
                                        axis,
                                        actualMovement
                                    );
                                    child.updateMatrixWorld();
                                }
                            }
                        });

                        if (ghostCabinetRef.current) {
                            movePreviewPanel(
                                ghostCabinetRef.current,
                                axis,
                                actualMovement
                            );
                        }
                    }

                    debouncedSave(selectedCabinetGroup);
                    updateArrowPositions();

                    return;
                }
            }

            const currentPos = selectedCabinetGroup.position.clone();
            const newPosition = currentPos.add(movementVector);
            let finalPosition = newPosition;

            if (direction === 'up' || direction === 'down') {
                finalPosition = applyVerticalClamping(
                    selectedCabinetGroup,
                    newPosition
                );
            }

            if (
                direction === 'left' ||
                direction === 'right' ||
                direction === 'front' ||
                direction === 'back'
            ) {
                const scene = selectedCabinetGroup.parent as THREE.Scene | null;
                const wallMesh = scene?.children.find(
                    (ch) =>
                        ch.name === ObjectNameMap.rectangularWall ||
                        ch.name === ObjectNameMap.lShapeWall
                ) as THREE.Mesh<
                    THREE.ExtrudeGeometry,
                    THREE.MeshStandardMaterial
                >;

                if (scene && roomType && wallMesh) {
                    const mouse = new THREE.Vector2(0, 0);
                    const clampedResult = productClamping(
                        selectedCabinetGroup,
                        wallMesh,
                        CameraSetup.camera,
                        mouse,
                        finalPosition,
                        roomType,
                        false,
                        true
                    );

                    if (clampedResult?.wasClamped) {
                        finalPosition = clampedResult.clamped;
                    }
                }
            }

            removeGhostCabinet();

            if (
                selectedCabinetGroup.userData &&
                typeof selectedCabinetGroup.userData.move === 'function'
            ) {
                selectedCabinetGroup.userData.move(finalPosition);
            } else {
                selectedCabinetGroup.position.copy(finalPosition);
            }

            selectedCabinetGroup.updateMatrixWorld(true);
            updateArrowPositions();
            debouncedSave(selectedCabinetGroup);
        },
        [
            selectedCabinetGroup,
            movementAmount,
            debouncedSave,
            updateArrowPositions,
            removeGhostCabinet,
            roomType,
        ]
    );

    // Handler for movement amount change
    const handleMovementAmountChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setMovementAmount(e.target.value);
        },
        []
    );

    // Update ghost cabinet preview when movement amount or direction changes
    useEffect(() => {
        if (
            !selectedCabinetGroup ||
            !activeDirection ||
            editorMode !== 'movement'
        ) {
            removeGhostCabinet();
            prevActiveDirectionRef.current = activeDirection;
            return;
        }

        // Update the ref to track direction changes
        prevActiveDirectionRef.current = activeDirection;

        // Get the movement amount value (default to 0 if empty or invalid)
        const amount = movementAmount === '' ? 0 : parseFloat(movementAmount);
        if (isNaN(amount)) {
            removeGhostCabinet();
            return;
        }

        // Get the current position vector
        const currentPos = selectedCabinetGroup.position.clone();

        // Create movement vector based on the arrow direction
        const movementVector = calculateMovementVector(activeDirection, amount);

        // Add the movement to current position
        const newPosition = currentPos.add(movementVector);

        // Apply vertical clamping for up/down movements
        let finalPosition = newPosition;
        if (activeDirection === 'up' || activeDirection === 'down') {
            finalPosition = applyVerticalClamping(
                selectedCabinetGroup,
                newPosition
            );
        }

        // Check for collision with other cabinets
        // Use a small negative buffer (-2mm) to allow "escape" movements when breaking away from snapped positions
        // This prevents cabinets from getting stuck when trying to move perpendicular to a snapped edge
        // Matches the behavior in keyboardControls.ts
        const escapeBuffer = -2;
        const allCabinets = cabinetGroupListRef?.current || [];
        const overlappingCabinets = checkCabinetOverlap(
            selectedCabinetGroup,
            finalPosition,
            allCabinets,
            escapeBuffer
        );

        const bothFloorProducts =
            overlappingCabinets?.some(
                (cabinet) =>
                    cabinet.userData.isFloorProduct || cabinet.userData.noBottom
            ) && selectedCabinetGroup?.userData.isFloorProduct;

        const hasOverlap =
            !!overlappingCabinets &&
            (bothFloorProducts || !selectedCabinetGroup.userData.ignoreOverlap);

        // Check wall boundaries
        const scene = selectedCabinetGroup.parent as THREE.Scene | null;
        const wallMesh = scene.children.find(
            (ch) =>
                ch.name === ObjectNameMap.rectangularWall ||
                ch.name === ObjectNameMap.lShapeWall
        ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;
        let isWithinWalls = true;
        if (scene && roomType && wallMesh) {
            const mouse = new THREE.Vector2(0, 0);
            const clampedResult = productClamping(
                selectedCabinetGroup,
                wallMesh,
                CameraSetup.camera,
                mouse,
                finalPosition,
                roomType,
                false,
                true // preventAutoRotation
            );

            // Check if position was clamped (meaning it went beyond walls)
            if (clampedResult?.wasClamped) {
                isWithinWalls = false;
                // Use clamped position for ghost preview
                finalPosition = clampedResult.clamped;
            }
        }

        // Movement is valid only if no overlap AND within walls
        // But if toggle is enabled, we still show the ghost (it will be red to indicate invalid position)
        const isValidMovement = !hasOverlap && isWithinWalls;

        // Always show ghost cabinet preview when editor is open
        renderGhostCabinet(isValidMovement, finalPosition, scene, true);

        handleFillerSwitching(
            cabinetGroupListRef.current,
            wallMesh.children,
            ghostCabinetRef.current
        );
    }, [
        movementAmount,
        activeDirection,
        selectedCabinetGroup,
        cabinetGroupListRef,
        renderGhostCabinet,
        removeGhostCabinet,
        roomType,
        editorMode,
    ]);

    // Function to handle closing the editor
    const handleClose = useCallback(() => {
        // Remove ghost cabinet when closing
        removeGhostCabinet();

        if (selectedCabinetGroup) {
            const userData = selectedCabinetGroup.userData as CustomUserData;
            // Show all arrows
            if (userData.movementArrows) {
                userData.movementArrows.forEach((arrow) => {
                    arrow.visible = true;
                });
            }
            // Reset arrow visibility states
            if (userData.resetArrowVisibility) {
                userData.resetArrowVisibility();
            }
        }
        setActiveDirection?.(null);
        setEditorMode?.(null);

        return true;
    }, [selectedCabinetGroup, removeGhostCabinet]);

    // Generic handler for all movement directions
    const handleMove = useCallback(() => {
        if (activeDirection) {
            handleMoveWithAmount(activeDirection);
            handleClose();
        }
    }, [activeDirection, handleMoveWithAmount, handleClose]);

    // Handler for Enter key press on input
    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleMoveWithAmount(activeDirection);
                handleClose();
            }
        },
        [activeDirection, handleMoveWithAmount, handleClose]
    );

    // Drag handlers for making the editor draggable
    const handleDragStart = useCallback(
        (e: React.MouseEvent) => {
            // Don't start drag if clicking on input, button, or close button
            const target = e.target as HTMLElement;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'BUTTON' ||
                target.closest('button')
            ) {
                return;
            }

            isDraggingRef.current = true;
            dragStartPosRef.current = {x: e.clientX, y: e.clientY};
            dragOffsetRef.current = {...editorPosition};

            const handleMouseMove = (e: MouseEvent) => {
                if (!isDraggingRef.current) return;

                const deltaX = e.clientX - dragStartPosRef.current.x;
                const deltaY = e.clientY - dragStartPosRef.current.y;

                setEditorPosition({
                    x: dragOffsetRef.current.x + deltaX,
                    y: dragOffsetRef.current.y + deltaY,
                });
            };

            const handleMouseUp = () => {
                isDraggingRef.current = false;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [editorPosition]
    );

    // Function to handle arrow click from the 3D scene
    useEffect(() => {
        if (selectedCabinetGroup) {
            // Reset arrow visibility when active group changes
            const userData = selectedCabinetGroup.userData as CustomUserData;
            if (userData.resetArrowVisibility) {
                userData.resetArrowVisibility();
            }

            // Add the onArrowClick handler to the activeGroup
            userData.onArrowClick = ({
                direction,
                screenX,
                screenY,
                arrowMesh,
            }: {
                direction: 'right' | 'left' | 'back' | 'front' | 'up' | 'down';
                screenX: number;
                screenY: number;
                arrowMesh: THREE.Mesh;
            }) => {
                const isAppliedPanel =
                    selectedCabinetGroup.userData.isPanelProduct ||
                    selectedCabinetGroup.userData.isManualPanel ||
                    selectedCabinetGroup.userData.isPanelProductWithWidth ||
                    selectedCabinetGroup.userData.isPanelProductWithLength;
                const isUpperProduct =
                    selectedCabinetGroup.userData.isUpperProduct;

                const mappedDirection = mapDirectionByOrientation(
                    direction,
                    userData.currentDirection,
                    isAppliedPanel,
                    isUpperProduct
                );

                setActiveDirection?.(mappedDirection);
                setEditorMode?.('movement');

                setMovementAmount('100');

                requestAnimationFrame(() => {
                    if (!selectedCabinetGroup) return;
                    const scene =
                        selectedCabinetGroup.parent as THREE.Scene | null;
                    if (!scene) return;

                    const currentPos = selectedCabinetGroup.position.clone();
                    const movementVector = calculateMovementVector(
                        mappedDirection,
                        100,
                        isAppliedPanel,
                        isUpperProduct
                    );

                    const targetPos = currentPos.add(movementVector);
                    removeGhostCabinet();
                    renderGhostCabinet(true, targetPos, scene, true);
                });

                if (userData.movementArrows) {
                    userData.movementArrows.forEach((arrow: THREE.Mesh) => {
                        if (arrow !== arrowMesh) {
                            arrow.visible = false;
                        }
                    });
                }

                const adjustedPos = adjustEditorPosition(
                    screenX,
                    screenY,
                    direction
                );

                setEditorPosition(adjustedPos);
            };

            userData.onDistanceClick = ({
                direction,
                screenX,
                screenY,
                measurement,
                bubble,
            }: {
                direction: string;
                screenX: number;
                screenY: number;
                measurement: string;
                measurementObject: THREE.Object3D;
                bubble?: DistanceBubbleRect;
            }) => {
                // Wall-distance pills are edited in place rather than in
                // the "Distance from wall" popup.
                if (bubble && isHorizontalWall(direction)) {
                    removeGhostCabinet();
                    setActiveDirection?.(null);
                    setEditorMode?.(null);
                    setBubbleEdit({
                        ...bubble,
                        wall: direction,
                        value: measurement,
                    });
                    return;
                }

                if (direction === 'floor-distance') {
                    setActiveDirection?.('down' as Direction);
                    setEditorMode?.('distance');
                    setEditorPosition({x: screenX, y: screenY});
                } else {
                    setActiveDirection?.(direction as Direction);
                    setEditorMode?.('distance');
                    setEditorPosition({x: screenX, y: screenY});
                }
            };

            return () => {
                removeGhostCabinet();

                if (selectedCabinetGroup?.userData) {
                    const userData =
                        selectedCabinetGroup.userData as CustomUserData;
                    if (userData.resetArrowVisibility) {
                        userData.resetArrowVisibility();
                    }
                }
            };
        }
    }, [selectedCabinetGroup, removeGhostCabinet, renderGhostCabinet]); // Remove activeDirection from dependencies

    // Note: We no longer apply wireframe mode to the original cabinet
    // The original cabinet stays solid while the ghost shows the preview position
    // This makes it clearer that the ghost is the preview, not the original cabinet
    useEffect(() => {
        // Ensure ghost cabinet meshes are visible when in movement mode
        if (selectedCabinetGroup && editorMode === 'movement') {
            if (ghostCabinetRef.current) {
                ghostCabinetRef.current.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.visible = true;
                    }
                });
            }
        }
    }, [selectedCabinetGroup, editorMode]);

    const bubbleEditor =
        bubbleEdit && selectedCabinetGroup ? (
            <DistanceBubbleEditor
                edit={bubbleEdit}
                cabinet={selectedCabinetGroup}
                cabinets={cabinetGroupListRef?.current ?? []}
                onClose={closeBubbleEdit}
            />
        ) : null;

    // Don't show editor if no direction is active
    if (!selectedCabinetGroup || !activeDirection || !editorMode) {
        return bubbleEditor;
    }

    return (
        <>
            {bubbleEditor}
            <EditorContainer
                ref={editorRef}
                style={{
                    top: `${editorPosition.y}px`,
                    left: `${editorPosition.x}px`,
                    display: activeDirection ? 'block' : 'none',
                }}>
                {isMobileDevice ? null : (
                    <CloseButton onClick={handleClose}>×</CloseButton>
                )}
                <DragHandle onMouseDown={handleDragStart} title="Drag to move">
                    ⋮
                </DragHandle>
                {isMobileDevice ? (
                    <MobileTitle>Move Cabinet</MobileTitle>
                ) : null}
                {editorMode === 'movement' && (
                    <>
                        <MovementControlContainer>
                            <MovementLabel>Move by: </MovementLabel>
                            <MovementRow>
                                <InputGroup>
                                    <MovementInput
                                        type="text"
                                        value={movementAmount}
                                        onChange={handleMovementAmountChange}
                                        onKeyDown={handleKeyPress}
                                    />
                                    <MeasurementDisplay>mm</MeasurementDisplay>
                                </InputGroup>
                                {isMobileDevice ? null : (
                                    <StepButton onClick={handleMove}>
                                        Move
                                    </StepButton>
                                )}
                            </MovementRow>
                        </MovementControlContainer>

                        {isMobileDevice ? (
                            <MobileControls className="control-buttons">
                                <Col xs={6}>
                                    <CancelButton
                                        noDialog={true}
                                        noRedirect={true}
                                        beforeRedirect={handleClose}
                                    />
                                </Col>
                                <Col xs={6}>
                                    <CBCButton
                                        onClick={handleMove}
                                        type="submit"
                                        iconName="Button-Tick.svg"
                                        className="job-button button-blue">
                                        Move
                                    </CBCButton>
                                </Col>
                            </MobileControls>
                        ) : null}
                    </>
                )}
                {editorMode === 'distance' && (
                    <DistanceEditor
                        activeDirection={activeDirection}
                        inputRefs={inputRefs}
                        onClose={handleClose}
                    />
                )}
            </EditorContainer>
        </>
    );
};

export default PositionEditor;

const MobileTitle = styled.strong`
    font-weight: bold;
    color: ${({theme}) => theme.colors.primary.main};
    padding-top: 15px;
`;

// Styled components
const EditorContainer = styled.div`
    position: fixed;
    background: #1e1e1e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    padding: 8px 12px;
    width: 200px;
    z-index: 2000;
    color: #f1f1f1;
    user-select: none;

    ${mobileLandscapeAndPortraitDevices`
        position: initial;
        background: white;
        box-shadow: none;
        display: flex !important;
        flex-direction: column;
        width: 100%;
        align-items: flex-start;
        padding: 0 10px;
        gap: 10px;
        border-radius: 0;
    `}

    ${mobileLandscape`
        background: transparent !important;
        height: 100%;    
    `}
`;

const DragHandle = styled.div`
    position: absolute;
    left: 4px;
    top: 4px;
    cursor: move;
    padding: 2px;
    color: #888;
    font-size: 10px;
    line-height: 1;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    &:hover {
        color: #aaa;
    }

    ${mobileLandscapeAndPortraitDevices`
        display: none;    
    `}
`;

const CloseButton = styled.button`
    position: absolute;
    top: -4px;
    right: -8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #444;
    border: none;
    color: #fff;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #555;
    }
`;

const MeasurementDisplay = styled.div`
    font-size: 11px;
    color: #888;
    margin-left: 5px;
    margin-right: auto;

    ${mobileLandscapeAndPortraitDevices`
        font-size: 0.9em;
        color: #585b5e;
        font-weight: 500;
    `}
`;

const InputGroup = styled.div`
    display: flex;
    align-items: center;

    ${mobileLandscapeAndPortraitDevices`
        flex: 1;
    `}
`;

const StepButton = styled.button`
    background: #444;
    border: none;
    border-radius: 3px;
    color: #f1f1f1;
    width: 55px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-weight: bold;
    margin-left: 4px;
    font-size: 12px;
    &:hover {
        background: #555;
    }
`;

const MovementControlContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;

    ${mobileLandscapeAndPortraitDevices`
        flex-direction: row;
        width: 100%;
        gap: 15%;
        padding-right: 20px;
        align-items: center;
    `}
`;

const MovementRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 6px;

    ${mobileLandscapeAndPortraitDevices`
        width: initial;
        flex: 1;
        margin-bottom: 0;
    `}
`;

const MovementLabel = styled.div`
    font-size: 12px;
    color: #aaa;

    ${mobileLandscapeAndPortraitDevices`
        font-size: 0.95em;
        color: rgb(var(--primary_colour));
        font-weight: 500;
    `}
`;

const MovementInput = styled.input`
    background: #333;
    border: 1px solid #444;
    border-radius: 3px;
    color: #f1f1f1;
    padding: 4px 8px;
    width: 80px;
    font-size: 13px;
    text-align: center;
    &:focus {
        outline: none;
        border-color: #666;
    }

    ${mobileLandscapeAndPortraitDevices`
        background: white;
        color: black;
        border: 2px solid rgb(var(--primary_colour));
        border-radius: 8px;
        flex: 1;
        padding: 6px 10px;
        text-align: left;
        font-size: 1em;
        color: #4b4f52;
    `}
`;
